/**
 * Daily Tasks Source Synchronizer & Deduplication Manager
 * Populates Daily Tasks from Google Sheet: 1omO-ZV6eNns56OpoifD3T75PrAouWhSf6bYdByHs-Cg (GID 1774295471)
 */
import type { DirectoryEntry } from '../types/api';
import { MASTER_DAILY_TASKS_ENTRIES } from '../config/dailyTasksMasterSource';

const DAILY_TASKS_STORAGE_KEY = 'ssd_daily_tasks_source_cache';

/**
 * Returns deduplicated daily task entries
 */
export function getDailyTasksList(): DirectoryEntry[] {
  let cached: any[] = [];
  try {
    const raw = localStorage.getItem(DAILY_TASKS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        cached = parsed;
      }
    }
  } catch {}

  const sourceList = cached.length > 0 ? cached : MASTER_DAILY_TASKS_ENTRIES;

  // Strict deduplication by normalized URL and name
  const seenUrls = new Set<string>();
  const uniqueEntries: DirectoryEntry[] = [];

  sourceList.forEach((rawEntry, idx) => {
    if (!rawEntry || !rawEntry.name) return;
    const normUrl = (rawEntry.url || '').toLowerCase().trim().replace(/\/$/, '');
    const sigKey = rawEntry.code ? `code:${rawEntry.code}` : `${normUrl}::${rawEntry.name.toLowerCase().trim()}`;

    if (!seenUrls.has(sigKey)) {
      seenUrls.add(sigKey);
      const entry: DirectoryEntry = {
        id: rawEntry.id || (rawEntry.code ? `daily-task-${rawEntry.code}` : `daily-task-${idx + 1}`),
        name: rawEntry.name,
        description: rawEntry.description || '',
        url: rawEntry.url || '',
        team: rawEntry.team || 'General',
        categories: Array.isArray(rawEntry.categories) && rawEntry.categories.length > 0 ? rawEntry.categories : ['Daily Task'],
        accessEmails: Array.isArray(rawEntry.accessEmails) ? rawEntry.accessEmails : [],
        customDisplayName: rawEntry.customDisplayName || undefined,
      };
      uniqueEntries.push(entry);
    }
  });

  return uniqueEntries;
}

/**
 * Background fetch from Cloudflare KV / Apps Script proxy to keep daily tasks sheet up to date
 */
export async function syncDailyTasksFromSource(): Promise<DirectoryEntry[]> {
  try {
    const res = await fetch('/api/support-requests?action=get_daily_tasks_source');
    if (res.ok) {
      const json = await res.json();
      if (json && json.success && Array.isArray(json.data) && json.data.length > 0) {
        const validList = json.data.filter((d: any) => d && d.name && d.url);
        if (validList.length > 0) {
          localStorage.setItem(DAILY_TASKS_STORAGE_KEY, JSON.stringify(validList));
          return validList;
        }
      }
    }
  } catch (e) {
    console.warn('[DailyTasks] Failed to sync latest source sheet in background:', e);
  }
  return getDailyTasksList();
}
